import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Banknote, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", surname: "", email: "", phone: "", address: "", city: "", postal_code: "", notes: "" };

export const CheckoutModal = () => {
  const { items, total, checkoutOpen, setCheckoutOpen, clear } = useCart();
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setCheckoutOpen(false);
    if (orderId) {
      setOrderId(null);
      setForm(initialForm);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    
    // Strict Validation
    if (!/^[0-9+\s]{9,15}$/.test(form.phone)) {
      toast.error("Por favor, introduce un número de teléfono válido.");
      return;
    }
    if (!/^\d{5}$/.test(form.postal_code)) {
      toast.error("El código postal debe tener 5 números (ej. 28001).");
      return;
    }

    setSending(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      });
      if (data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {checkoutOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
          data-testid="checkout-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl bg-noir-soft border border-white/10 my-8"
          >
            <button data-testid="checkout-close-button" onClick={close} className="absolute top-5 right-5 text-neutral-500 hover:text-gold transition-colors z-10" aria-label="Cerrar">
              <X size={20} />
            </button>

            <form onSubmit={submit} className="px-8 sm:px-14 py-12" data-testid="checkout-form">
              <span className="font-script-lux text-gold text-3xl">Casi tuyo</span>
              <h3 className="font-serif-lux text-3xl tracking-[0.15em] uppercase text-white mt-2">Datos de entrega</h3>
              <p className="text-neutral-500 text-sm font-light mt-3">Envío a domicilio (+3.00 €).</p>

              <div className="grid sm:grid-cols-2 gap-5 mt-10">
                <div>
                  <label className="lux-label" htmlFor="co-name">Nombre</label>
                  <input id="co-name" data-testid="checkout-name-input" required className="lux-input" value={form.name} onChange={set("name")} placeholder="María" />
                </div>
                <div>
                  <label className="lux-label" htmlFor="co-surname">Apellidos</label>
                  <input id="co-surname" data-testid="checkout-surname-input" required className="lux-input" value={form.surname} onChange={set("surname")} placeholder="García López" />
                </div>
                <div>
                  <label className="lux-label" htmlFor="co-email">Email</label>
                  <input id="co-email" data-testid="checkout-email-input" required type="email" className="lux-input" value={form.email} onChange={set("email")} placeholder="tucorreo@gmail.com" />
                </div>
                <div>
                  <label className="lux-label" htmlFor="co-phone">Teléfono</label>
                  <input id="co-phone" data-testid="checkout-phone-input" required type="tel" className="lux-input" value={form.phone} onChange={set("phone")} placeholder="600 123 456" />
                </div>
                <div className="sm:col-span-2">
                  <label className="lux-label" htmlFor="co-address">Dirección de entrega</label>
                  <input id="co-address" data-testid="checkout-address-input" required className="lux-input" value={form.address} onChange={set("address")} placeholder="Calle, número, piso, puerta" />
                </div>
                <div>
                  <label className="lux-label" htmlFor="co-city">Ciudad</label>
                  <input id="co-city" data-testid="checkout-city-input" required className="lux-input" value={form.city} onChange={set("city")} placeholder="Madrid" />
                </div>
                <div>
                  <label className="lux-label" htmlFor="co-postal">Código postal</label>
                  <input id="co-postal" data-testid="checkout-postal-input" required className="lux-input" value={form.postal_code} onChange={set("postal_code")} placeholder="28001" />
                </div>
                <div className="sm:col-span-2">
                  <label className="lux-label" htmlFor="co-notes">Notas (opcional)</label>
                  <textarea id="co-notes" data-testid="checkout-notes-input" rows={2} className="lux-input resize-none" value={form.notes} onChange={set("notes")} placeholder="Horario preferido, indicaciones..." />
                </div>
              </div>

              <div className="flex items-center gap-4 border border-gold/30 bg-gold/5 px-5 py-4 mt-8">
                <Banknote size={22} className="text-gold shrink-0" />
                <p className="text-xs text-neutral-300 font-light leading-relaxed">
                  <span className="text-gold uppercase tracking-widest">Pago Seguro con Tarjeta</span><br />
                  Serás redirigido a Stripe para completar tu pago de forma segura.
                </p>
              </div>

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block">Total a pagar (incl. envío)</span>
                  <span className="font-serif-lux text-3xl text-gold" data-testid="checkout-total">{(total + 3).toFixed(2)} €</span>
                </div>
                <button
                  data-testid="checkout-submit-button"
                  type="submit"
                  disabled={sending || items.length === 0}
                  className="group relative bg-gold text-black text-xs uppercase tracking-[0.25em] px-8 py-4 overflow-hidden disabled:opacity-50"
                >
                  <span className="relative z-10 transition-colors duration-500 group-hover:text-gold">{sending ? "Procesando..." : "Ir a pagar"}</span>
                  <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
